import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Save, CheckCircle, Lock, HelpCircle, Plus, Snowflake, GitBranch, Wallet, TrendingUp, Layers, Percent,
} from 'lucide-react';
import Breadcrumb from '../../components/Common/Breadcrumb';
import HelpModal from '../../components/HelpModal/HelpModal';
import { burnExpenseService } from '../../services/burnExpenseService';
import applicationParametersService from '../../services/applicationParametersService';
import { toast } from 'react-toastify';
import '../../components/ConfirmationModal/ConfirmationModal.css';
import './FinancialYearBudget.css';

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const CATEGORY_GROUPS = ['Salary', 'Infra', 'Office', 'Marketing', 'Travel', 'Compliance', 'Other'];

const buildMonthKeys = (startYear, startMonth) => {
  const sm = Math.min(12, Math.max(1, Number(startMonth) || 4));
  const keys = [];
  for (let i = 0; i < 12; i += 1) {
    const monthNum = ((sm - 1 + i) % 12) + 1;
    let year = startYear;
    if (sm !== 1 && monthNum < sm) year = startYear + 1;
    keys.push({
      label: MONTH_SHORT[monthNum - 1],
      yyyymm: `${year}-${String(monthNum).padStart(2, '0')}`,
    });
  }
  return keys;
};

const fyDateBounds = (startYear, startMonth) => {
  const sm = Math.min(12, Math.max(1, Number(startMonth) || 4));
  const startDate = new Date(startYear, sm - 1, 1);
  let endYear;
  let endMonthIndex;
  if (sm === 1) {
    endYear = startYear;
    endMonthIndex = 11;
  } else {
    endYear = startYear + 1;
    endMonthIndex = sm - 2;
  }
  const endDate = new Date(endYear, endMonthIndex + 1, 0);
  return { startDate, endDate };
};

const generateFYOptions = () => {
  const opts = [];
  const currentYear = new Date().getFullYear();
  for (let y = currentYear - 2; y <= currentYear + 3; y++) {
    opts.push(`${y}-${String(y + 1).slice(-2)}`);
  }
  return opts;
};

/** Current FY label (e.g. 2026-27) from calendar date + FY start month (1–12) */
const getCurrentFYLabel = (startMonth = 4, asOf = new Date()) => {
  const sm = Math.min(12, Math.max(1, Number(startMonth) || 4));
  const y = asOf.getFullYear();
  const m = asOf.getMonth() + 1;
  let startYear;
  if (sm === 1) startYear = y;
  else if (m >= sm) startYear = y;
  else startYear = y - 1;
  return `${startYear}-${String(startYear + 1).slice(-2)}`;
};

const formatINR = (v) => new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
}).format(v || 0);

const statusLabel = (s) => {
  if (s === 'approved') return 'Approved';
  if (s === 'frozen') return 'Frozen';
  return 'Draft';
};

const isAdmin = () => {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    const r = u?.role;
    if (Array.isArray(r)) return r.some((x) => (x?.name || x?.roleName || '').toLowerCase() === 'admin');
    return (r?.name || r?.roleName || r || '').toString().toLowerCase().includes('admin');
  } catch { return false; }
};

const FinancialYearBudget = () => {
  const navigate = useNavigate();
  const [fyOptions] = useState(generateFYOptions);
  const [selectedFY, setSelectedFY] = useState(() => getCurrentFYLabel(4));
  const [fiscalYearStartMonth, setFiscalYearStartMonth] = useState(4);
  const [budget, setBudget] = useState(null);
  const [revisions, setRevisions] = useState([]);
  const [viewRevision, setViewRevision] = useState(null); // null = current
  const [categories, setCategories] = useState([]);
  const [gridData, setGridData] = useState({});
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', group: 'Other', isRecurringDefault: false });
  const [addingCategory, setAddingCategory] = useState(false);

  const canApprove = isAdmin();
  const startYear = selectedFY ? parseInt(selectedFY.split('-')[0], 10) : null;

  const months = useMemo(
    () => (startYear ? buildMonthKeys(startYear, fiscalYearStartMonth) : []),
    [startYear, fiscalYearStartMonth],
  );

  const { startDate, endDate } = useMemo(() => {
    if (!startYear) return { startDate: null, endDate: null };
    return fyDateBounds(startYear, fiscalYearStartMonth);
  }, [startYear, fiscalYearStartMonth]);

  const loadParams = useCallback(async () => {
    try {
      const r = await applicationParametersService.get();
      const m = Number(r?.data?.general?.fiscalYearStartMonth ?? r?.general?.fiscalYearStartMonth);
      const startMonth = (Number.isFinite(m) && m >= 1 && m <= 12) ? m : 4;
      setFiscalYearStartMonth(startMonth);
      setSelectedFY(getCurrentFYLabel(startMonth));
    } catch (_) {
      setSelectedFY(getCurrentFYLabel(4));
    }
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      const r = await burnExpenseService.getCategories({ isActive: true });
      if (r?.success && r.data?.length) setCategories(r.data);
      else setCategories([]);
    } catch (e) {
      toast.error('Failed to load categories');
    }
  }, []);

  const loadSummary = useCallback(async (budgetId) => {
    if (!budgetId) { setSummary(null); return; }
    try {
      const r = await burnExpenseService.getFinancialYearBudgetSummary(budgetId);
      if (r?.success) setSummary(r.data);
      else setSummary(null);
    } catch {
      setSummary(null);
    }
  }, []);

  const loadBudget = useCallback(async () => {
    if (!selectedFY) return;
    setLoading(true);
    try {
      const r = await burnExpenseService.getFinancialYearBudgets({ financialYear: selectedFY });
      if (r?.fiscalYearStartMonth) setFiscalYearStartMonth(r.fiscalYearStartMonth);
      if (r?.success && r.data?.length) {
        const sorted = [...r.data].sort((a, b) => (b.revision || 1) - (a.revision || 1));
        setRevisions(sorted);
        const current = sorted.find((b) => b.isCurrent === true)
          || sorted.find((b) => b.isCurrent !== false)
          || sorted[0];
        const target = viewRevision != null
          ? sorted.find((b) => (b.revision || 1) === Number(viewRevision)) || current
          : current;
        setBudget(target);
        const lines = await burnExpenseService.getBudgetLines(target._id);
        if (lines?.success && lines.data) {
          const g = {};
          lines.data.forEach((l) => {
            const cid = l.categoryId?._id || l.categoryId;
            if (cid) g[`${cid}_${l.month}`] = l.budgetAmount;
          });
          setGridData(g);
        } else {
          setGridData({});
        }
        await loadSummary(target._id);
      } else {
        setBudget(null);
        setRevisions([]);
        setViewRevision(null);
        setGridData({});
        setSummary(null);
      }
    } catch (e) {
      toast.error('Failed to load budget');
      setBudget(null);
      setRevisions([]);
      setGridData({});
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [selectedFY, loadSummary, viewRevision]);

  useEffect(() => { loadParams(); loadCategories(); }, [loadParams, loadCategories]);
  useEffect(() => { setViewRevision(null); }, [selectedFY]);
  useEffect(() => { loadBudget(); }, [loadBudget]);

  const revisionNum = budget?.revision || 1;
  const isViewingHistorical = budget && budget.isCurrent === false;
  const canEdit = budget?.status === 'draft' && !isViewingHistorical;

  const getCellValue = (categoryId, monthYYYYMM) => gridData[`${categoryId}_${monthYYYYMM}`] ?? 0;

  const setCellValue = (categoryId, monthYYYYMM, value) => {
    if (!canEdit) return;
    const n = parseFloat(value);
    setGridData((g) => ({ ...g, [`${categoryId}_${monthYYYYMM}`]: isNaN(n) ? 0 : Math.max(0, n) }));
  };

  const getRowTotal = (categoryId) =>
    months.reduce((s, mo) => s + (parseFloat(getCellValue(categoryId, mo.yyyymm)) || 0), 0);

  const getColTotal = (yyyymm) =>
    categories.reduce((s, c) => s + (parseFloat(getCellValue(c._id, yyyymm)) || 0), 0);

  const getGrandTotal = () => categories.reduce((s, c) => s + getRowTotal(c._id), 0);

  const liveAvgMonthly = getGrandTotal() / 12;

  const handleCreate = async () => {
    if (!selectedFY) { toast.error('Select a financial year'); return; }
    setSaving(true);
    try {
      const r = await burnExpenseService.createFinancialYearBudget({ financialYear: selectedFY });
      if (r?.success) {
        toast.success('Budget created');
        if (r.fiscalYearStartMonth) setFiscalYearStartMonth(r.fiscalYearStartMonth);
        loadBudget();
      } else toast.error(r?.message || 'Failed to create');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to create');
    } finally {
      setSaving(false);
    }
  };

  const buildLinesForSave = () => {
    const lines = [];
    categories.forEach((c) => {
      months.forEach((mo) => {
        lines.push({
          categoryId: c._id,
          month: mo.yyyymm,
          budgetAmount: parseFloat(getCellValue(c._id, mo.yyyymm)) || 0,
        });
      });
    });
    return lines;
  };

  const handleSaveDraft = async () => {
    if (budget?.status !== 'draft' || !budget?._id) return;
    setSaving(true);
    try {
      const r = await burnExpenseService.upsertBudgetLines(budget._id, buildLinesForSave());
      if (r?.success) {
        toast.success('Draft saved');
        loadBudget();
      } else toast.error(r?.message || 'Failed to save');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleFreeze = async () => {
    if (!budget?._id || budget.status !== 'draft') return;
    setStatusBusy(true);
    try {
      await burnExpenseService.upsertBudgetLines(budget._id, buildLinesForSave());
      const r = await burnExpenseService.freezeFinancialYearBudget(budget._id);
      if (r?.success) {
        toast.success('Budget frozen');
        loadBudget();
      } else toast.error(r?.message || 'Failed to freeze');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to freeze');
    } finally {
      setStatusBusy(false);
    }
  };

  const handleNextRevision = async () => {
    if (!budget?._id || !['frozen', 'approved'].includes(budget.status)) return;
    setStatusBusy(true);
    try {
      const r = await burnExpenseService.createNextFinancialYearBudgetRevision(budget._id);
      if (r?.success) {
        toast.success(r.message || `Revision ${r.data?.revision} created`);
        setViewRevision(null);
        loadBudget();
      } else toast.error(r?.message || 'Failed to create next revision');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to create next revision');
    } finally {
      setStatusBusy(false);
    }
  };

  const handleApprove = async () => {
    if (!budget?._id || budget.status === 'approved') return;
    setStatusBusy(true);
    try {
      if (budget.status === 'draft') {
        await burnExpenseService.upsertBudgetLines(budget._id, buildLinesForSave());
      }
      const r = await burnExpenseService.approveFinancialYearBudget(budget._id);
      if (r?.success) {
        toast.success('Budget approved');
        loadBudget();
      } else toast.error(r?.message || 'Failed to approve');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to approve');
    } finally {
      setStatusBusy(false);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.name.trim()) {
      toast.error('Category name is required');
      return;
    }
    setAddingCategory(true);
    try {
      const r = await burnExpenseService.createCategory({
        name: newCategory.name.trim(),
        group: newCategory.group,
        isRecurringDefault: !!newCategory.isRecurringDefault,
        isActive: true,
      });
      if (r?.success) {
        toast.success('Category added');
        setShowAddCategory(false);
        setNewCategory({ name: '', group: 'Other', isRecurringDefault: false });
        await loadCategories();
      } else toast.error(r?.message || 'Failed to add category');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to add category');
    } finally {
      setAddingCategory(false);
    }
  };

  const breadcrumbItems = [
    { label: 'Expenses', path: '/expenses', onClick: () => navigate('/expenses') },
    { label: 'Transactions', path: '/expenses?tab=transactions', onClick: () => navigate('/expenses?tab=transactions') },
    { label: 'Financial Year Budget', path: '/expenses/financial-year-budget', isCurrent: true },
  ];

  const fyStartLabel = MONTH_SHORT[(fiscalYearStartMonth || 4) - 1];

  const helpContent = (
    <>
      <h3>Financial Year Budget</h3>
      <p>Define monthly burn budgets by category for the company financial year. The FY calendar follows <strong>Application Parameters → Fiscal Year Start Month</strong> (currently {fyStartLabel}).</p>
      <h3>Status flow</h3>
      <ul>
        <li><strong>Draft</strong> — editable; save amounts anytime</li>
        <li><strong>Frozen</strong> — locked; use <strong>Next Revision</strong> to unfreeze into a new editable copy</li>
        <li><strong>Approved</strong> — final lock for reporting; changes require a next revision</li>
      </ul>
      <h3>Revisions</h3>
      <p>Each freeze/approve cycle is a revision (Rev 1, Rev 2, …). Prior revisions stay locked for history; only the current revision can be edited when it is Draft.</p>
      <h3>Add category</h3>
      <p>Use <strong>Add Category</strong> to create a burn category and budget a new row without leaving this screen.</p>
    </>
  );

  const displayTotalBudget = canEdit ? getGrandTotal() : (summary?.totalBudget ?? getGrandTotal());
  const displayActual = summary?.totalActual ?? 0;
  const displayVariance = displayActual - displayTotalBudget;

  return (
    <div className="sales-dashboard fy-budget-page">
      <div className="breadcrumb-section">
        <Breadcrumb
          items={breadcrumbItems}
          showBackButton={false}
          rightSlot={(
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
              onClick={() => setShowHelp(true)}
              title="Help"
            >
              <HelpCircle size={18} />
              <span className="d-none d-sm-inline">Help</span>
            </button>
          )}
        />
      </div>

      <div className="page-header">
        <div className="page-title">
          <h1>Financial Year Budget</h1>
          <p>
            FY starts in <strong>{fyStartLabel}</strong> (from Application Parameters)
          </p>
        </div>
      </div>

      <div className="fy-budget-toolbar">
        <label>Financial Year</label>
        <select
          className="fy-budget-select"
          value={selectedFY}
          onChange={(e) => setSelectedFY(e.target.value)}
        >
          <option value="">Select FY</option>
          {fyOptions.map((fy) => (
            <option key={fy} value={fy}>{fy}</option>
          ))}
        </select>
        {selectedFY && (
          <>
            <span className="fy-budget-date-range">
              {startDate?.toLocaleDateString('en-IN')} – {endDate?.toLocaleDateString('en-IN')}
            </span>
            {budget && (
              <>
                <span className={`fy-budget-status-badge ${budget.status}`}>
                  {statusLabel(budget.status)}
                </span>
                <span className="fy-budget-rev-badge" title="Budget revision">
                  Rev {revisionNum}
                </span>
                {revisions.length > 1 && (
                  <select
                    className="fy-budget-select fy-budget-rev-select"
                    value={viewRevision != null ? String(viewRevision) : String(revisions.find((b) => b.isCurrent !== false)?.revision || revisions[0].revision || 1)}
                    onChange={(e) => setViewRevision(Number(e.target.value))}
                    title="View revision"
                  >
                    {revisions.map((rev) => (
                      <option key={rev._id} value={rev.revision || 1}>
                        Rev {rev.revision || 1}
                        {rev.isCurrent !== false ? ' (current)' : ''}
                        {' — '}
                        {statusLabel(rev.status)}
                      </option>
                    ))}
                  </select>
                )}
              </>
            )}
            {!budget && (
              <button type="button" className="fy-budget-btn create" onClick={handleCreate} disabled={saving}>
                Create Budget
              </button>
            )}
          </>
        )}
      </div>

      {loading && (
        <div className="fy-budget-loading">
          <div className="spinner" role="status" aria-label="Loading" />
          <p>Loading budget...</p>
        </div>
      )}

      {!loading && selectedFY && budget && (
        <>
          <div className="fy-budget-summary-grid">
            <div className="fy-budget-card">
              <div className="fy-budget-card__icon"><Layers size={16} /></div>
              <div className="fy-budget-card__value">{categories.length}</div>
              <div className="fy-budget-card__label">Categories</div>
              <div className="fy-budget-card__sub">Budget line items</div>
            </div>
            <div className="fy-budget-card">
              <div className="fy-budget-card__icon"><Wallet size={16} /></div>
              <div className="fy-budget-card__value">{formatINR(displayTotalBudget)}</div>
              <div className="fy-budget-card__label">Total Budget</div>
              <div className="fy-budget-card__sub">
                Avg {formatINR(canEdit ? liveAvgMonthly : (summary?.avgMonthlyBudget || liveAvgMonthly))}/mo
              </div>
            </div>
            <div className="fy-budget-card">
              <div className="fy-budget-card__icon"><TrendingUp size={16} /></div>
              <div className="fy-budget-card__value">{formatINR(displayActual)}</div>
              <div className="fy-budget-card__label">Actual Spend (FY)</div>
              <div className="fy-budget-card__sub">Approved burn entries</div>
            </div>
            <div className={`fy-budget-card${displayVariance > 0 ? ' is-over' : ' is-under'}`}>
              <div className="fy-budget-card__icon"><Percent size={16} /></div>
              <div className="fy-budget-card__value">{formatINR(displayVariance)}</div>
              <div className="fy-budget-card__label">Variance</div>
              <div className="fy-budget-card__sub">
                {displayTotalBudget > 0
                  ? `${((displayVariance / displayTotalBudget) * 100).toFixed(1)}% of budget`
                  : 'No budget yet'}
              </div>
            </div>
          </div>

          <div className="fy-budget-actions">
            {canEdit && (
              <>
                <button type="button" className="fy-budget-btn save" onClick={handleSaveDraft} disabled={saving}>
                  <Save size={14} /> Save Draft
                </button>
                <button type="button" className="fy-budget-btn freeze" onClick={handleFreeze} disabled={statusBusy}>
                  <Snowflake size={14} /> Freeze
                </button>
                {canApprove && (
                  <button type="button" className="fy-budget-btn approve" onClick={handleApprove} disabled={statusBusy}>
                    <CheckCircle size={14} /> Approve
                  </button>
                )}
              </>
            )}
            {(budget.status === 'frozen' || budget.status === 'approved') && !isViewingHistorical && (
              <>
                <button
                  type="button"
                  className="fy-budget-btn reopen"
                  onClick={handleNextRevision}
                  disabled={statusBusy}
                  title="Create next revision as a new draft (keeps this revision locked)"
                >
                  <GitBranch size={14} /> Next Revision
                </button>
                {budget.status === 'frozen' && canApprove && (
                  <button type="button" className="fy-budget-btn approve" onClick={handleApprove} disabled={statusBusy}>
                    <CheckCircle size={14} /> Approve
                  </button>
                )}
                <span className="fy-budget-locked">
                  <Lock size={14} />
                  {budget.status === 'frozen' ? 'Frozen' : 'Approved'} — editing locked
                </span>
              </>
            )}
            {isViewingHistorical && (
              <span className="fy-budget-locked">
                <Lock size={14} /> Viewing Rev {revisionNum} (historical) — switch to current to edit
              </span>
            )}
            <button
              type="button"
              className="fy-budget-btn add-cat"
              onClick={() => setShowAddCategory(true)}
              disabled={!canEdit}
              title={canEdit ? 'Add burn category' : 'Create next revision (draft) to add categories'}
            >
              <Plus size={14} /> Add Category
            </button>
          </div>

          <div className="fy-budget-grid-wrap">
            <table className="fy-budget-table">
              <thead>
                <tr>
                  <th className="category-col">Category</th>
                  {months.map((mo) => (
                    <th key={mo.yyyymm} className="numeric month-col">{mo.label}</th>
                  ))}
                  <th className="numeric annual-col">Annual</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((c) => (
                  <tr key={c._id}>
                    <td className="category-cell">
                      {c.name}
                      {c.group && <span className="category-group">({c.group})</span>}
                    </td>
                    {months.map((mo) => {
                      const val = getCellValue(c._id, mo.yyyymm);
                      return (
                        <td key={mo.yyyymm} className="numeric">
                          {canEdit ? (
                            <input
                              type="number"
                              step="1"
                              min="0"
                              className="fy-budget-input"
                              value={val || ''}
                              onChange={(e) => setCellValue(c._id, mo.yyyymm, e.target.value)}
                            />
                          ) : (
                            <span className="fy-budget-cell-value">{formatINR(val)}</span>
                          )}
                        </td>
                      );
                    })}
                    <td className="numeric annual-cell">{formatINR(getRowTotal(c._id))}</td>
                  </tr>
                ))}
                <tr className="total-row">
                  <td className="category-cell">Total</td>
                  {months.map((mo) => (
                    <td key={mo.yyyymm} className="numeric">{formatINR(getColTotal(mo.yyyymm))}</td>
                  ))}
                  <td className="numeric">{formatINR(getGrandTotal())}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}

      {!loading && selectedFY && !budget && categories.length > 0 && (
        <div className="alert alert-info">Create a budget for FY {selectedFY} to start editing.</div>
      )}

      {!loading && selectedFY && !budget && categories.length === 0 && (
        <div className="alert alert-warning">
          Add burn categories first (use Add Category after creating a budget, or Burn Category Master).
        </div>
      )}

      {showAddCategory && (
        <div className="confirmation-modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowAddCategory(false)}>
          <div className="confirmation-modal fy-add-cat-modal" role="dialog" aria-modal="true">
            <div className="confirmation-modal-header">
              <h3 className="confirmation-modal-title mb-0">Add Budget Category</h3>
              <button type="button" className="confirmation-modal-close" onClick={() => setShowAddCategory(false)}>×</button>
            </div>
            <div className="confirmation-modal-content">
              <div className="mb-3">
                <label className="form-label">Name *</label>
                <input
                  className="form-control"
                  value={newCategory.name}
                  onChange={(e) => setNewCategory((c) => ({ ...c, name: e.target.value }))}
                  placeholder="e.g. Travel & Conveyance"
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Group *</label>
                <select
                  className="form-select"
                  value={newCategory.group}
                  onChange={(e) => setNewCategory((c) => ({ ...c, group: e.target.value }))}
                >
                  {CATEGORY_GROUPS.map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="fy-cat-recurring"
                  checked={newCategory.isRecurringDefault}
                  onChange={(e) => setNewCategory((c) => ({ ...c, isRecurringDefault: e.target.checked }))}
                />
                <label className="form-check-label" htmlFor="fy-cat-recurring">Recurring by default</label>
              </div>
            </div>
            <div className="confirmation-modal-footer">
              <button type="button" className="confirmation-modal-cancel" onClick={() => setShowAddCategory(false)}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={handleAddCategory} disabled={addingCategory}>
                {addingCategory ? 'Adding…' : 'Add Category'}
              </button>
            </div>
          </div>
        </div>
      )}

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title="Financial Year Budget – Help">
        {helpContent}
      </HelpModal>
    </div>
  );
};

export default FinancialYearBudget;
